import React, { useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { Send, MessageSquare } from 'lucide-react';

const SimpleChatInterface = () => {
  const {
    channels,
    activeChannel,
    loading,
    sendMessage,
    switchChannel,
    getChannelMessages
  } = useChat();

  const [newMessage, setNewMessage] = useState('');

  const activeMessages = getChannelMessages(activeChannel?.id);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChannel) return;

    await sendMessage(activeChannel.id, newMessage.trim());
    setNewMessage('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[600px] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex h-full">
        {/* Sidebar des canaux - Version simplifiée */}
        <div className="w-80 border-r border-gray-200 bg-gray-50">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Conversations</h2>
            <p className="text-sm text-gray-600">Sélectionnez un canal</p>
          </div>
          
          <div className="p-4">
            {channels.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Aucune conversation disponible</p>
              </div>
            ) : (
              <div className="space-y-2">
                {channels.map((channel) => (
                  <button
                    key={channel.id}
                    onClick={() => switchChannel(channel.id)}
                    className={`w-full text-left p-3 rounded-lg transition-colors duration-200 ${
                      activeChannel?.id === channel.id
                        ? 'bg-primary text-white'
                        : 'bg-white hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        activeChannel?.id === channel.id ? 'bg-white/20' : 'bg-primary/10'
                      }`}>
                        <span className={`text-sm font-medium ${
                          activeChannel?.id === channel.id ? 'text-white' : 'text-primary'
                        }`}>
                          {channel.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-medium truncate ${
                          activeChannel?.id === channel.id ? 'text-white' : 'text-gray-900'
                        }`}>
                          {channel.name}
                        </h3>
                        <p className={`text-xs truncate ${
                          activeChannel?.id === channel.id ? 'text-white/80' : 'text-gray-600'
                        }`}>
                          {channel.type === 'course' ? 'Canal de cours' :
                           channel.type === 'direct' ? 'Message privé' :
                           channel.type === 'group' ? 'Groupe d\'étude' : 'Annonces'}
                        </p>
                      </div>
                      {channel.unreadCount > 0 && (
                        <div className={`text-xs rounded-full w-5 h-5 flex items-center justify-center ${
                          activeChannel?.id === channel.id 
                            ? 'bg-white text-primary' 
                            : 'bg-primary text-white'
                        }`}>
                          {channel.unreadCount}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Zone de chat principale */}
        <div className="flex-1 flex flex-col">
          {activeChannel ? (
            <>
              {/* Header du chat */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">
                      {activeChannel.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{activeChannel.name}</h3>
                    <p className="text-sm text-gray-600">
                      {activeChannel.type === 'direct' ? 'Message privé' : 
                       activeChannel.type === 'course' ? 'Canal du cours' :
                       activeChannel.type === 'group' ? 'Groupe d\'étude' : 'Annonces'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {activeMessages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Aucun message dans cette conversation</p>
                    <p className="text-sm">Envoyez le premier message !</p>
                  </div>
                ) : (
                  activeMessages.map((message) => (
                    <div key={message.id} className={`flex gap-3 ${message.author.id === 'current_user' ? 'flex-row-reverse' : ''}`}>
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-medium">
                          {message.author.avatar}
                        </span>
                      </div>
                      
                      <div className={`flex-1 max-w-xs lg:max-w-md ${message.author.id === 'current_user' ? 'text-right' : ''}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {message.author.name}
                          </span>
                          {message.author.role === 'instructor' && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                              Instructeur
                            </span>
                          )}
                          <span className="text-xs text-gray-500">
                            {new Date(message.timestamp).toLocaleTimeString('fr-FR', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </span>
                        </div>
                        
                        <div className={`p-3 rounded-lg ${
                          message.author.id === 'current_user' 
                            ? 'bg-primary text-white' 
                            : 'bg-gray-100 text-gray-900'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Zone de saisie */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <form onSubmit={handleSendMessage} className="flex items-end gap-3">
                  <div className="flex-1">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Tapez votre message..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      rows="1"
                      style={{ minHeight: '44px', maxHeight: '120px' }}
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="bg-primary text-white p-3 rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2">Sélectionnez une conversation</h3>
                <p className="text-sm">Choisissez un canal dans la sidebar pour commencer à discuter</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleChatInterface;
